import React, { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';

let activeHost = null;
let nextAlertId = 1;
const pendingAlerts = [];

function enqueueAlert(alert) {
  const item = { id: nextAlertId++, ...alert };
  if (activeHost) {
    activeHost(item);
  } else {
    pendingAlerts.push(item);
  }
}

export function showStyledAlert(message, options = {}) {
  enqueueAlert({
    type: 'alert',
    message: String(message ?? ''),
    severity: options.severity || 'info',
    confirmLabel: options.confirmLabel || 'OK',
  });
}

export function showStyledConfirm(message, options = {}) {
  return new Promise((resolve) => {
    enqueueAlert({
      type: 'confirm',
      message: String(message ?? ''),
      severity: options.severity || 'warning',
      confirmLabel: options.confirmLabel || 'Confirm',
      cancelLabel: options.cancelLabel || 'Cancel',
      confirmButtonSx: options.confirmButtonSx,
      resolve,
    });
  });
}

export default function StyledAlertHost() {
  const [queue, setQueue] = useState([]);
  const current = queue[0];

  useEffect(() => {
    const receiveAlert = (item) => setQueue((previous) => [...previous, item]);
    activeHost = receiveAlert;

    if (pendingAlerts.length > 0) {
      const queuedBeforeMount = pendingAlerts.splice(0, pendingAlerts.length);
      setQueue((previous) => [...previous, ...queuedBeforeMount]);
    }

    return () => {
      if (activeHost === receiveAlert) activeHost = null;
    };
  }, []);

  const closeCurrent = (confirmed = false) => {
    if (current?.type === 'confirm') current.resolve(Boolean(confirmed));
    setQueue((previous) => previous.slice(1));
  };

  return (
    <Dialog
      open={Boolean(current)}
      onClose={() => closeCurrent(false)}
      maxWidth="xs"
      fullWidth
      aria-describedby={current ? `styled-alert-message-${current.id}` : undefined}
      sx={{ zIndex: 4000 }}
    >
      <DialogContent sx={{ pt: 3 }}>
        <Alert
          severity={current?.severity || 'info'}
          variant="outlined"
          sx={{ alignItems: 'flex-start' }}
        >
          <Box
            id={current ? `styled-alert-message-${current.id}` : undefined}
            sx={{ whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}
          >
            {current?.message}
          </Box>
        </Alert>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {current?.type === 'confirm' && (
          <Button color="inherit" onClick={() => closeCurrent(false)}>
            {current.cancelLabel}
          </Button>
        )}
        <Button
          variant="contained"
          color={current?.severity === 'error' ? 'error' : 'primary'}
          sx={current?.confirmButtonSx}
          onClick={() => closeCurrent(true)}
          autoFocus
        >
          {current?.confirmLabel || 'OK'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
